/**
 * Fuente de verdad del contenido comercial de la landing.
 * REGLA: no se inventan datos del producto.
 */

export interface Tomo {
  num: string
  name: string
  desc: string
  cover: string
}
export interface RecipeAttr {
  title: string
  desc: string
  /** Marca el atributo destacado (Keto + Sin TACC a la vez). */
  highlight?: boolean
}
export interface RecipeSample {
  name: string
  /** Tomo al que pertenece la receta. */
  tomo: string
  image: string
  width: number
  height: number
}
export interface AudienceItem {
  title: string
  desc: string
}
export interface Bono {
  num: string
  name: string
  desc: string
  cover: string
}
export interface Faq {
  q: string
  a: string
}

/** Nombre comercial único del producto. */
export const PRODUCT_NAME = '500+ Recetas Keto + Sin TACC'

/** Formato monetario único de la landing: siempre "$00.000". */
export const PRICE = {
  previous: '$29.990',
  current: '$14.990',
  note: 'Precio en pesos argentinos · Pago único',
} as const

/** Medios de pago (circuito argentino). */
export const PAYMENT_TEXT =
  'Pagá en pesos argentinos con Mercado Pago o con las opciones disponibles en el checkout.'

export const PRODUCT_FACTS: string[] = [
  '+500 recetas',
  '6 tomos digitales',
  'Keto + Sin TACC',
  '+4 bonos',
]

/** Hoy → Con la colección (única sección de problema/transformación). */
export const TRANSFORM_BEFORE: string[] = [
  'Abrís la heladera y no sabés qué cocinar.',
  'Comés siempre los mismos tres o cuatro platos.',
  'Las recetas que encontrás son keto, pero llevan harina con gluten.',
  'Piden ingredientes que acá no se consiguen.',
  'Te aburrís y terminás saliéndote de tu alimentación.',
]

export const TRANSFORM_AFTER: string[] = [
  'Abrís la colección y elegís entre más de 500 recetas.',
  'Tenés variedad para cada momento del día.',
  'Cada receta es Keto y Sin TACC al mismo tiempo.',
  'Ingredientes habituales en Argentina y una guía de sustituciones.',
  'Comés rico todos los días y sostenés tu alimentación.',
]

/** Los 6 tomos reales de la colección. */
export const TOMOS: Tomo[] = [
  {
    num: '1',
    name: 'Desayunos y Meriendas',
    desc: 'Opciones para arrancar el día y para la tarde, sin harinas con gluten.',
    cover: 'https://cdn.magicpatterns.com/uploads/ao1mgA7jKnCGRdUtWsPyVd/portada_tomo_1.png',
  },
  {
    num: '2',
    name: 'Almuerzos',
    desc: 'Platos para resolver el mediodía, en casa o para llevar.',
    cover: 'https://cdn.magicpatterns.com/uploads/4xQfGobhMz2WpLLoZjCiwo/portada_tomo_2.png',
  },
  {
    num: '3',
    name: 'Cenas',
    desc: 'Preparaciones para la noche, sin repetir siempre lo mismo.',
    cover: 'https://cdn.magicpatterns.com/uploads/ugU3qPntvu1rXm4v9qLYyQ/portada_tomo_3.png',
  },
  {
    num: '4',
    name: 'Snacks, Picadas y Comidas Rápidas',
    desc: 'Para picar algo, armar una picada o resolver una comida en poco tiempo.',
    cover: 'https://cdn.magicpatterns.com/uploads/1RraCLz41YkJf9K2auRo4p/portada_tomo_4.png',
  },
  {
    num: '5',
    name: 'Panes, Masas y Horneados Salados',
    desc: 'Panes, masas y horneados salados con alternativas como harina de almendras.',
    cover: 'https://cdn.magicpatterns.com/uploads/4rt3s3unhmRog9yAXFbgKG/portada_tomo_5.png',
  },
  {
    num: '6',
    name: 'Salsas, Aderezos y Básicos',
    desc: 'Salsas, aderezos y preparaciones base para acompañar tus platos.',
    cover: 'https://cdn.magicpatterns.com/uploads/p46ySzuB4ZxMTV7Fh5EP5h/portada_tomo_6.png',
  },
]

/** Atributos que cumple cada receta (tomados de las tapas de la colección). */
export const RECIPE_ATTRS: RecipeAttr[] = [
  {
    title: 'Keto + Sin TACC a la vez',
    desc: 'No tenés que elegir entre una cosa y la otra: cada receta cumple con las dos.',
    highlight: true,
  },
  { title: 'Bajas en carbos', desc: 'Pensadas para no sacarte de tu alimentación.' },
  { title: 'Altas en grasas saludables', desc: 'Palta, aceite de oliva, frutos secos, huevo.' },
  { title: 'Nutritivas', desc: 'Platos completos, no soluciones de emergencia.' },
  { title: 'Listas en minutos', desc: 'Preparaciones simples para el día a día.' },
  {
    title: 'Ingredientes accesibles',
    desc: 'Ingredientes habituales en Argentina y una guía de sustituciones para adaptar tus preparaciones.',
  },
]

/** Las 3 páginas reales de muestra (se muestran tal cual, sin transcribir). */
export const RECIPE_SAMPLES: RecipeSample[] = [
  {
    name: 'Merluza al horno con crema y espinaca',
    tomo: 'Tomo 3 · Cenas',
    image:
      'https://cdn.magicpatterns.com/uploads/9dS3ybsLq221imjiuLFKfo/muestra_03_merluza_espinaca.png',
    width: 1500,
    height: 2121,
  },
  {
    name: 'Ensalada César Keto con Pollo y Crocantes de Queso',
    tomo: 'Tomo 2 · Almuerzos',
    image:
      'https://cdn.magicpatterns.com/uploads/gWnfPNgaXXBtEmNsjju1H6/muestra_02_ensalada_cesar.png',
    width: 1500,
    height: 2121,
  },
  {
    name: 'Panqueque keto de almendras',
    tomo: 'Tomo 1 · Desayunos y Meriendas',
    image:
      'https://cdn.magicpatterns.com/uploads/iEFpY8RYvabis1SozWK9kg/muestra_01_panqueque_almendras.png',
    width: 1500,
    height: 2121,
  },
]

/** Para quién es la colección. */
export const AUDIENCE: AudienceItem[] = [
  {
    title: 'Hacés Keto y te aburriste',
    desc: 'Necesitás variedad real para no comer siempre lo mismo.',
  },
  {
    title: 'Tenés que comer Sin TACC',
    desc: 'Buscás recetas sin harinas de trigo, avena, cebada ni centeno.',
  },
  {
    title: 'Cocinás para toda la familia',
    desc: 'Querés platos que le gusten a todos, no un menú aparte.',
  },
  {
    title: 'Tenés poco tiempo',
    desc: 'Preferís preparaciones simples y rápidas de resolver.',
  },
  {
    title: 'Recién empezás',
    desc: 'Querés algo ordenado que te diga qué cocinar en cada momento del día.',
  },
]

/** Los 4 bonos reales (las portadas se muestran tal como fueron entregadas). */
export const BONOS: Bono[] = [
  {
    num: '1',
    name: 'Lista Maestra de Compras Keto + Sin TACC',
    desc: 'Organizá tus compras y tené a mano los ingredientes que necesitás.',
    cover: 'https://cdn.magicpatterns.com/uploads/86SfVcQEXicCr5scM4AqAP/bono_01_portada.png',
  },
  {
    num: '2',
    name: 'Guía de Sustituciones Inteligentes',
    desc: 'Consultá alternativas según los ingredientes que tengas disponibles.',
    cover: 'https://cdn.magicpatterns.com/uploads/4S2rE6RwrbUMFAmBAtX1xR/bono_02_portada.png',
  },
  {
    num: '3',
    name: '30 Menús Keto + Sin TACC listos',
    desc: 'Organizá tus comidas con ideas de menús listas para consultar.',
    cover: 'https://cdn.magicpatterns.com/uploads/h7w96fksYWAKjkTvPG9Mqb/bono_03_portada.png',
  },
  {
    num: '4',
    name: 'Planificador Semanal + Lista de Compras imprimible',
    desc: 'Planificá qué cocinar y anotá tus compras en un mismo recurso.',
    cover: 'https://cdn.magicpatterns.com/uploads/n1oSwCpcCszckMKsWQwK3c/bono_04_portada.png',
  },
]

/** Contenido de la colección, dentro de la sección de oferta. */
export const OFFER_INCLUDES: string[] = [
  '6 tomos digitales con más de 500 recetas',
  'Todas las recetas Keto y Sin TACC al mismo tiempo',
  'Ingredientes habituales en Argentina',
  'Desayunos y meriendas, almuerzos, cenas, snacks y picadas, panes y masas, salsas y básicos',
  'Recetas con ingredientes y preparación paso a paso',
  'Formato digital para leer desde el celular, la tablet o la compu',
]

export const FAQS: Faq[] = [
  {
    q: '¿Las recetas son Keto y Sin TACC al mismo tiempo?',
    a: 'Sí. Cada receta de los 6 tomos está pensada para cumplir con las dos cosas a la vez: baja en carbohidratos y sin harinas con gluten. Se usan alternativas como la harina de almendras.',
  },
  {
    q: '¿Necesito ingredientes difíciles de conseguir?',
    a: 'La colección usa ingredientes habituales en Argentina. Además, la Guía de Sustituciones Inteligentes te ayuda a adaptar tus preparaciones según lo que tengas disponible.',
  },
  {
    q: '¿Sirve si recién empiezo con Keto?',
    a: 'Sí. Las recetas tienen ingredientes y preparación paso a paso, y están ordenadas por tomo para que sepas qué cocinar en cada comida.',
  },
  {
    q: '¿Son recetas complicadas?',
    a: 'No. Son preparaciones simples, pensadas para una cocina hogareña. Podés ver 3 recetas completas en la sección “Mirá las recetas por dentro”.',
  },
  {
    q: '¿Cómo recibo la colección?',
    a: 'En formato digital, inmediatamente después de la compra. No se envía nada físico.',
  },
  {
    q: '¿Puedo verla desde el celular?',
    a: 'Sí. La abrís desde el celular, la tablet o la computadora, y la tenés siempre a mano en la cocina.',
  },
  {
    q: '¿Es un pago único? ¿Cómo pago?',
    a: `Sí, es una compra única de AR${PRICE.current}, sin suscripciones ni pagos mensuales. ${PAYMENT_TEXT}`,
  },
  {
    q: '¿Qué bonos están incluidos?',
    a: 'Los 4 bonos vienen incluidos sin costo adicional: Lista Maestra de Compras Keto + Sin TACC, Guía de Sustituciones Inteligentes, 30 Menús Keto + Sin TACC listos y Planificador Semanal + Lista de Compras imprimible.',
  },
]
